import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { closeIcon } from '../../icons'
import { getFormattedDate } from '../../helpers/timeHelper'
import { getRfidTagEvents } from '../../modules/Rfid'
import { parseHexColor } from '../../utils/colorUtils'
import Loading from '../common/Loading'
import QrTagEventsMap from './QrTagEventsMap'
import './DetailModalCommon.css'
import './DetailQrTagModal.css'
import './DetailRfidTagModal.css'

type Props = {
  closeModalFunction: (arg?: any) => void
  modalProps: {
    rfidTag: IRfidTag
    group: IGroup
    refreshList: () => Promise<void> | void
  }
}

const hasLocation = (
  event: IRfidTagEvent,
): event is IRfidTagEvent & { latitude: number; longitude: number } => (
  event.latitude != null && event.longitude != null
)

const formatLocation = (event: IRfidTagEvent) => {
  if (!hasLocation(event)) return '位置情報なし'
  return `${event.latitude}, ${event.longitude}`
}

const formatRecorder = (event: IRfidTagEvent) => {
  const recorder = event.user?.name || event.user?.email
  return recorder || '記録者不明'
}

const buildTimelineItemStyle = (
  event: IRfidTagEvent,
): React.CSSProperties | undefined => {
  const parsedColor = parseHexColor(event.event_type_color)
  if (!parsedColor) return undefined

  return {
    '--timeline-marker-color': `#${parsedColor.rgb}`,
  } as React.CSSProperties
}

const DetailRfidTagModal = ({
  closeModalFunction,
  modalProps,
}: Props) => {
  const { rfidTag, group, refreshList } = modalProps
  const [events, setEvents] = useState<IRfidTagEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => () => {
    isMountedRef.current = false
  }, [])

  const fetchEvents = useCallback(async () => {
    if (!isMountedRef.current) return

    try {
      setLoading(true)
      const result = await getRfidTagEvents(group.id, rfidTag.id, { per: 10 })
      if (!isMountedRef.current) return
      setEvents(result)
      setError(null)
    } catch (fetchError) {
      if (!isMountedRef.current) return
      console.error('Failed to fetch RFID tag events', fetchError)
      setEvents([])
      setError('RFIDタグイベントの取得に失敗しました')
    } finally {
      if (isMountedRef.current) setLoading(false)
    }
  }, [group.id, rfidTag.id])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  const eventsWithLocation = useMemo(
    () => events.filter(hasLocation),
    [events],
  )

  useEffect(() => {
    if (!eventsWithLocation.length) {
      setSelectedEventId(null)
      return
    }
    if (
      selectedEventId == null
      || !eventsWithLocation.some((event) => event.id === selectedEventId)
    ) {
      setSelectedEventId(eventsWithLocation[0]!.id)
    }
  }, [eventsWithLocation, selectedEventId])

  const reloadAll = async () => {
    await fetchEvents()
    await refreshList()
  }

  return (
    <div className="detail-modal-container" data-testid="detail-rfid-tag-modal">
      <Loading show={loading} />
      <div className="overlay" onClick={closeModalFunction} />
      <div className="detail-base">
        <div className="scrollable">
          <div className="title-row">
            <div className="detail-title">RFIDタグ詳細</div>
            <div>
              <button
                onClick={closeModalFunction}
                className="close-button"
                type="button"
              >
                <img src={closeIcon} alt="close-button" />
              </button>
            </div>
          </div>
          <div className="rfid-tag-modal-main">
            <div className="rfid-tag-summary">
              <div className="rfid-tag-name">{rfidTag.name}</div>
              <div className="rfid-tag-identifier">
                <span className="label">RFID識別子</span>
                <span>{rfidTag.identifier}</span>
              </div>
              <div className="rfid-tag-payload">
                <span className="label">Payload</span>
                <span>{rfidTag.payload || '-'}</span>
              </div>
            </div>
            <div className="rfid-tag-actions">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={reloadAll}
              >
                最新情報を取得
              </button>
            </div>
            {error && <div className="rfid-tag-error">{error}</div>}
            <QrTagEventsMap
              events={events}
              selectedEventId={selectedEventId}
              onSelectEvent={setSelectedEventId}
            />
            <div className="rfid-tag-timeline">
              <div className="timeline-title">最新イベント（最大10件）</div>
              {events.length === 0 && !error && (
                <div className="timeline-empty">記録がありません</div>
              )}
              <ul className="timeline-list">
                {events.map((event) => {
                  const eventType = event.event_type || 'イベント種別不明'
                  const timelineBody = (
                    <>
                      <div className="timeline-item-header">
                        <div className="timeline-event-type">{eventType}</div>
                        <div className="timeline-occurred-at">
                          {event.occurred_at ? getFormattedDate(event.occurred_at) : '-'}
                        </div>
                      </div>
                      <div className="timeline-body">
                        <div>
                          <span className="label">場所</span>
                          <span>{formatLocation(event)}</span>
                        </div>
                        <div>
                          <span className="label">記録者</span>
                          <span>{formatRecorder(event)}</span>
                        </div>
                        <div>
                          <span className="label">Device</span>
                          <span>{event.device_id ? `ID: ${event.device_id}` : 'なし'}</span>
                        </div>
                        <div>
                          <span className="label">メモ</span>
                          <span>{event.notes || 'なし'}</span>
                        </div>
                      </div>
                    </>
                  )

                  return (
                    <li
                      key={event.id}
                      className={[
                        'timeline-item',
                        hasLocation(event) ? 'timeline-item--selectable' : '',
                        hasLocation(event) && selectedEventId === event.id
                          ? 'timeline-item--selected'
                          : '',
                      ].filter(Boolean).join(' ')}
                      style={buildTimelineItemStyle(event)}
                    >
                      {hasLocation(event) ? (
                        <button
                          type="button"
                          className="timeline-item__interactive"
                          onClick={() => setSelectedEventId(event.id)}
                        >
                          {timelineBody}
                        </button>
                      ) : (
                        <div className="timeline-item__content">{timelineBody}</div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DetailRfidTagModal
